import { useCallback, useEffect, useRef, useState } from 'react';
import { PAUZE_MS_STANDAARD } from '@tolk/shared';
import { opnameOpties, opnemenBeschikbaar } from '../lib/spraak';
import { audioSessieType, noteer } from '../lib/meetlog';

/**
 * Inspreken: ALLEEN opnemen. De tekst komt van de server.
 *
 * WAAROM ZO, EN NIET MET DE BROWSERHERKENNING. Op een Pixel 9A is op
 * 4 september 2026 gemeten dat spraakherkenning en opname NIET tegelijk
 * kunnen - in beide volgordes niet. MediaRecorder pakt de microfoon
 * exclusief en dan valt de herkenning stil.
 *
 * De server schrijft de audio uit EN vertaalt hem in een aanroep. Dat kost
 * ongeveer EUR 0,00008 per zin extra, gemeten - verwaarloosbaar naast de
 * EUR 0,0034 die de spraak kost. En het levert drie dingen op:
 *   - je eigen stem gaat mee naar de ander
 *   - het werkt ook in browsers zonder spraakherkenning (Firefox, iOS)
 *   - een pijplijn in plaats van twee
 *
 * De stilte wordt hier gemeten met de Web Audio API, want zonder herkenning is
 * er niets dat zegt wanneer je klaar bent.
 */

/**
 * De ONDERGRENS van de stiltedrempel. Schaal 0 tot 1.
 *
 * De drempel zelf wordt gemeten, niet vastgezet. Een vaste waarde werkt in een
 * stille kamer en niet in een trein: daar ligt het achtergrondgeluid boven de
 * drempel en stopt de opname nooit. Andersom, in een heel stille kamer met een
 * zachte spreker, kapt een te hoge drempel je middenin een zin af.
 *
 * Deze waarde is er alleen om te voorkomen dat de drempel in een doodstille
 * opname op nul uitkomt.
 */
const DREMPEL_ONDERGRENS = 0.008;

/**
 * Hoeveel luider dan de omgeving iets moet zijn om als praten te tellen.
 *
 * Gemeten over de eerste halve seconde, voordat je begint. Praten ligt in de
 * praktijk een veelvoud boven het achtergrondniveau; drie keer is genoeg om
 * ruis buiten te sluiten zonder een zachte spreker af te kappen.
 */
const DREMPEL_FACTOR = 3;

/** Zo lang meten we de omgeving voordat de drempel vaststaat. */
const IJKTIJD_MS = 500;

/**
 * Hoe lang na het laatste eigen geluid de microfoon nog niet te vertrouwen is.
 *
 * Een speaker houdt op met trillen nadat het fragment afgelopen is, en de
 * echodemping van de browser loopt achter. Meteen weer meten zou die naijling
 * als praten tellen - en dan stopt de opname alsnog nooit.
 */
const ECHO_NASLEEP_MS = 400;

/** Zo vaak kijken of er nog geluid is. */
const METING_MS = 100;

/** Langer dan dit is geen zin meer maar een monoloog. Hard stoppen. */
const MAX_OPNAME_MS = 60_000;

/**
 * Grootste opname die we versturen, in bytes.
 *
 * De server accepteert 2 MB. Deze grens ligt lager zodat de CLIENT het merkt en
 * kan zeggen wat er aan de hand is - een bericht dat bij de server afketst,
 * verbreekt daar de verbinding en dan lijkt de app gewoon te hangen.
 */
const MAX_OPNAME_BYTES = 1_500_000;

export interface OpnameResultaat {
  audio: { mimeType: string; base64: string; durationMs: number };
}

/** Wat er mis is met de microfoon. De TEKST komt uit de gekozen taal. */
export type SpreekProbleem =
  | 'geen_opname'
  | 'gedempt'
  | 'niets_opgenomen'
  | 'niets_gehoord'
  | 'geweigerd'
  | 'geen_microfoon'
  | 'in_gebruik'
  | 'geen_https'
  | 'te_lang'
  | 'onbekend';

export interface Spreker {
  luistert: boolean;
  /** 0 tot 1. Voor de balkjes: laat zien dat de microfoon je hoort. */
  niveau: number;
  /** Hoe lang je al praat, in seconden. */
  verstreken: number;
  /** Zoveel seconden mag een opname duren. Voor de aftelling op het scherm. */
  maxSeconden: number;
  fout: SpreekProbleem | null;
  kanOpnemen: boolean;
  start: () => void;
  stop: () => void;
  /**
   * Laat de microfoon los, zodat iOS het geluid weer naar de luidspreker
   * stuurt. Aanroepen vlak voor er iets gaat klinken. De volgende zin vraagt
   * de microfoon dan opnieuw aan; dat is de prijs.
   */
  laatMicrofoonLos: () => void;
}

export interface SprekenOpties {
  /** Wordt aangeroepen zodra de opname klaar is. */
  opKlaar: (resultaat: OpnameResultaat) => void;
  /**
   * true zolang de app ZELF geluid afspeelt.
   *
   * WAAROM DIT ERBIJ MOET. De stiltemeter hoort niet alleen jou, hij hoort de
   * kamer - en daar staat een speaker in die de vertaling van de ander
   * afspeelt. Zonder dit signaal ging het op 5 september 2026 twee keer mis:
   *
   *   - De opname stopte nooit. Elke meting lag boven de drempel, dus was het
   *     nooit stil, dus telde de stilteklok nooit door.
   *   - "Niets gehoord" na een zin die er wel degelijk was. Viel het ijken
   *     samen met het afspelen, dan werd de vertaling als omgeving gemeten en
   *     kwam de drempel boven een gewone spreekstem te liggen.
   *
   * `echoCancellation` van de browser haalt op een Mac met open speakers lang
   * niet alles weg, dus dat alleen is niet genoeg.
   */
  speeltAf?: boolean;
  /**
   * Stilte na je laatste woord voordat de zin klaar is, in ms. Komt uit de
   * kamerinstelling, die de beheerder zet; zie shared/src/pauze.ts. Wordt bij
   * ELKE meting opnieuw gelezen, dus een wijziging telt meteen, ook midden in
   * een opname.
   */
  pauzeMs?: number;
}

function blobNaarBase64(blob: Blob): Promise<string> {
  return new Promise((klaar, mis) => {
    const lezer = new FileReader();
    lezer.onerror = () => mis(new Error('opname niet te lezen'));
    lezer.onload = () => {
      const resultaat = String(lezer.result);
      // Een data-URL is "data:audio/webm;base64,AAAA". Alleen het deel na de
      // komma is de eigenlijke inhoud.
      klaar(resultaat.slice(resultaat.indexOf(',') + 1));
    };
    lezer.readAsDataURL(blob);
  });
}

export function useSpreken(opties: SprekenOpties): Spreker {
  const [luistert, setLuistert] = useState(false);
  const [niveau, setNiveau] = useState(0);
  const [verstreken, setVerstreken] = useState(0);
  const [fout, setFout] = useState<SpreekProbleem | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const stroomRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const stukkenRef = useRef<Blob[]>([]);
  const meterRef = useRef<number | undefined>(undefined);
  const stilteVanafRef = useRef<number | null>(null);
  const drempelRef = useRef(DREMPEL_ONDERGRENS);
  const ijkingRef = useRef<number[]>([]);
  const beginRef = useRef(0);
  const hoordeIetsRef = useRef(false);
  /** Tot wanneer de microfoon nog naklinkt van ons eigen geluid. */
  const echoTotRef = useRef(0);
  /** Wanneer het ijken begon. Pas gezet als er GEEN eigen geluid speelt. */
  const ijkVanafRef = useRef<number | null>(null);
  /** Staat de drempel eenmaal vast, dan wordt hij niet opnieuw geijkt. */
  const ijkKlaarRef = useRef(false);
  /**
   * Het stilste dat we deze opname gemeten hebben, buiten ons eigen geluid om.
   *
   * VANGNET ONDER HET IJKEN. Het ijken kijkt een halve seconde en zit ernaast
   * als je juist in die halve seconde begint te praten: dan telt je eigen stem
   * als omgeving en ligt de drempel te hoog. Deze vloer loopt de hele opname
   * door en zoekt het werkelijke achtergrondniveau, want praten is luid en
   * duwt een minimum niet omhoog. De drempel mag daardoor altijd nog OMLAAG,
   * nooit omhoog - te laag hoort hooguit een deur dichtvallen, te hoog hoort
   * jou niet.
   */
  const vloerRef = useRef(Number.POSITIVE_INFINITY);

  const optiesRef = useRef(opties);
  optiesRef.current = opties;

  /**
   * Einde van EEN opname. De microfoon blijft van ons, maar staat stil.
   *
   * HIER STOND `t.stop()`, EN DAT GAF DE BLAUWE BALK BIJ ELKE ZIN. Een spoor
   * dat je stopt, is op. De volgende zin vroeg dus opnieuw om de microfoon, en
   * Chrome op iOS zet bij ELKE aanvraag zijn toestemmingsbalk bovenin - ook
   * vlak na een eerdere toestemming, gemeten op 11 september 2026. In een
   * tolkgesprek spreek je om de paar zinnen; dan is dat geen melding meer
   * maar een muur.
   *
   * `enabled = false` levert stilte in plaats van je stem, dus tussen twee
   * zinnen wordt er niets opgenomen. WAT HET NIET DOET, EN DAT IS GEMETEN: de
   * luidspreker teruggeven. Zolang er een levend spoor bestaat, aan of uit,
   * stuurt iOS al het geluid naar het oortje. Daarom laat RoomPage de
   * microfoon echt LOS vlak voor het afspelen; zie laatMicrofoonLos.
   *
   * WAT HET KOST: het microfoonbolletje van iOS blijft staan zolang jij aan
   * het woord bent, en de balk komt terug bij je eerste zin na een vertaling.
   * Een balk per beurt dus, niet per zin. Minder kan niet in Chrome op iOS.
   */
  const opnameKlaar = useCallback(() => {
    if (meterRef.current !== undefined) window.clearInterval(meterRef.current);
    meterRef.current = undefined;
    const hadStroom = stroomRef.current !== null;
    stroomRef.current?.getAudioTracks().forEach((t) => (t.enabled = false));
    void ctxRef.current?.close();
    ctxRef.current = null;
    // DE TEKST IS MEEVERANDERD MET DE CODE. Hier stond "sporen gestopt", en dat
    // is precies wat er sinds deze opzet NIET meer gebeurt: het spoor blijft
    // leven, het staat alleen stil. Een meetlog dat het oude verhaal vertelt,
    // stuurt de volgende meting de verkeerde kant op.
    if (hadStroom) noteer(`microfoon: stilgezet, spoor blijft (sessie=${audioSessieType()})`);
    setNiveau(0);
    setVerstreken(0);
  }, []);

  /**
   * De microfoon echt uit. Sporen gestopt, stroom weg.
   *
   * Twee aanleidingen: het verlaten van de kamer, en het moment vlak voor de
   * app iets gaat afspelen. Hier moet `stop()` staan en niet `enabled = false`:
   * gemeten op 11 september 2026 geeft iOS de luidspreker pas terug als het
   * spoor GESTOPT is. Uitzetten is niet genoeg. Stoppen en meteen afspelen
   * werkt, met ongeveer een derde seconde vertraging op het eerste geluid.
   */
  const microfoonLoslaten = useCallback(() => {
    opnameKlaar();
    const hadStroom = stroomRef.current !== null;
    stroomRef.current?.getTracks().forEach((t) => t.stop());
    stroomRef.current = null;
    // De regel die er in het meetlog het meeste toe doet: hierna hoort het
    // geluid uit de luidspreker te komen, en hoort de volgende zin een nieuwe
    // aanvraag te zijn. Staat hier GEEN regel vlak voor een fragment, dan is
    // het loslaten overgeslagen en klinkt de vertaling in het oortje.
    if (hadStroom) noteer(`microfoon: losgelaten, sporen gestopt (sessie=${audioSessieType()})`);
  }, [opnameKlaar]);

  /**
   * Voor RoomPage: laat de microfoon los omdat er zo iets gaat klinken.
   *
   * NIET tijdens een opname. Dan zou de zin die je aan het inspreken bent
   * halverwege afbreken. RoomPage stelt afspelen sowieso uit tot de microfoon
   * dicht is, dus dit is een vangnet en geen pad.
   */
  const laatMicrofoonLos = useCallback(() => {
    if (recorderRef.current !== null) return;
    microfoonLoslaten();
  }, [microfoonLoslaten]);

  /**
   * De microfoon, klaar voor gebruik.
   *
   * Is hij er al en leeft hij nog, dan wordt hij hergebruikt en alleen weer
   * aangezet - zonder nieuwe aanvraag, dus zonder balk. Een spoor dat gestopt
   * is, komt nooit meer tot leven; dan is een nieuwe aanvraag de enige weg.
   *
   * DE TWEE LOGREGELS ZIJN HET MEETINSTRUMENT VAN DEZE HELE OPZET. De vraag
   * "komt de balk per zin of per beurt" is aan de buitenkant alleen te zien
   * doordat Chrome hem toont; in het log is het het verschil tussen deze twee
   * regels. Zie je binnen een beurt tweemaal "nieuwe aanvraag", dan werkt het
   * hergebruik niet en komt de balk alsnog bij elke zin.
   */
  const microfoonKlaar = useCallback(async (): Promise<MediaStream> => {
    const bestaand = stroomRef.current;
    if (bestaand !== null && bestaand.getAudioTracks().some((t) => t.readyState === 'live')) {
      bestaand.getAudioTracks().forEach((t) => (t.enabled = true));
      noteer(`microfoon: hergebruikt, geen nieuwe aanvraag (sessie=${audioSessieType()})`);
      return bestaand;
    }
    noteer(`microfoon: nieuwe aanvraag, balk verwacht (sessie=${audioSessieType()})`);
    const nieuw = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    stroomRef.current = nieuw;
    return nieuw;
  }, []);

  const afronden = useCallback(async () => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder === null) return;

    const duur = Date.now() - beginRef.current;
    const blob = await new Promise<Blob | null>((klaar) => {
      recorder.onstop = () => {
        const stukken = stukkenRef.current;
        klaar(stukken.length > 0 ? new Blob(stukken, { type: recorder.mimeType }) : null);
      };
      if (recorder.state !== 'inactive') recorder.stop();
      else klaar(null);
    });

    opnameKlaar();
    setLuistert(false);

    // Niets gehoord? Dan geen aanroep doen - betalen voor het uitschrijven
    // van stilte is zonde. Maar wel ZEGGEN dat er niets gehoord is: stilweg
    // niets doen laat de gebruiker denken dat de app kapot is, terwijl zijn
    // microfoon uit staat.
    if (blob === null || blob.size === 0) {
      setFout('niets_opgenomen');
      stukkenRef.current = [];
      return;
    }
    if (!hoordeIetsRef.current) {
      setFout('niets_gehoord');
      stukkenRef.current = [];
      return;
    }

    try {
      const base64 = await blobNaarBase64(blob);
      // VOOR het versturen toetsen. Een te groot bericht ketst bij de server
      // af, verbreekt daar de verbinding, en dan lijkt de app te hangen zonder
      // dat er ergens iets staat.
      if (base64.length > MAX_OPNAME_BYTES) {
        setFout('te_lang');
        stukkenRef.current = [];
        return;
      }
      optiesRef.current.opKlaar({
        audio: { mimeType: blob.type, base64, durationMs: duur },
      });
    } catch {
      setFout('onbekend');
    }
    stukkenRef.current = [];
  }, [opnameKlaar]);

  const start = useCallback(() => {
    if (luistert) return;
    if (!opnemenBeschikbaar()) {
      setFout('geen_opname');
      return;
    }

    setFout(null);
    stukkenRef.current = [];
    stilteVanafRef.current = null;
    hoordeIetsRef.current = false;
    beginRef.current = Date.now();
    drempelRef.current = DREMPEL_ONDERGRENS;
    ijkingRef.current = [];
    ijkVanafRef.current = null;
    ijkKlaarRef.current = false;
    vloerRef.current = Number.POSITIVE_INFINITY;
    echoTotRef.current = 0;
    setVerstreken(0);

    // De aanvraag zelf staat nu in microfoonKlaar, samen met het hergebruik en
    // de twee logregels die dat verschil zichtbaar maken.
    void microfoonKlaar()
      .then((stroom) => {
        stroomRef.current = stroom;
        noteer(`microfoon: open voor opname (sessie=${audioSessieType()})`);

        // Formaat EN bitsnelheid uit een plek. Zie opnameOpties in lib/spraak.
        const recorder = new MediaRecorder(stroom, opnameOpties());
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) stukkenRef.current.push(e.data);
        };
        recorder.start();
        recorderRef.current = recorder;
        setLuistert(true);

        // Een spoor dat uit staat geeft wel een stroom, maar alleen stilte.
        // Dat gebeurt bij een gedempte microfoon of een headset die niet aan
        // staat - en zonder deze controle merk je dat pas na tien seconden
        // praten.
        const sporen = stroom.getAudioTracks();
        if (sporen.length === 0 || sporen[0]?.enabled === false || sporen[0]?.muted === true) {
          setFout('gedempt');
        }

        // Stiltemeter. Zonder herkenning is er niets dat zegt wanneer je klaar
        // bent, dus meten we het geluidsniveau zelf.
        const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctx === undefined) return;
        const ctx = new Ctx();
        ctxRef.current = ctx;
        const analyse = ctx.createAnalyser();
        analyse.fftSize = 1024;
        ctx.createMediaStreamSource(stroom).connect(analyse);
        const buffer = new Float32Array(analyse.fftSize);

        meterRef.current = window.setInterval(() => {
          analyse.getFloatTimeDomainData(buffer);
          // Effectieve waarde: gemiddelde luidheid, niet de piek. Een enkele
          // tik telt dan niet als praten.
          let som = 0;
          for (const v of buffer) som += v * v;
          const sterkte = Math.sqrt(som / buffer.length);
          setNiveau(Math.min(1, sterkte * 8));

          const nu = Date.now();
          setVerstreken(Math.floor((nu - beginRef.current) / 1000));

          // ONS EIGEN GELUID EERST. Speelt de app af, dan hoort de microfoon
          // die speaker en is deze meting van ons en niet van de spreker. Ze
          // telt dan nergens voor mee: niet als praten, niet als stilte, niet
          // als ijking. De stilteklok bevriest en begint straks bij nul.
          if (optiesRef.current.speeltAf === true) {
            echoTotRef.current = nu + ECHO_NASLEEP_MS;
          }
          if (nu < echoTotRef.current) {
            stilteVanafRef.current = null;
            ijkingRef.current = [];
            ijkVanafRef.current = null;
            return;
          }

          // Eerste halve seconde ZONDER eigen geluid: de omgeving opmeten, nog
          // niets beslissen. De klok begint pas hier, en niet bij het begin van
          // de opname: viel het begin samen met het afspelen, dan hebben we die
          // halve seconde aan de vertaling van de ander gemeten.
          if (!ijkKlaarRef.current) {
            if (ijkVanafRef.current === null) ijkVanafRef.current = nu;
            if (nu - ijkVanafRef.current < IJKTIJD_MS) {
              ijkingRef.current.push(sterkte);
              return;
            }
            // De MEDIAAN en niet het gemiddelde: een deur die dichtvalt tijdens
            // het ijken zou het gemiddelde omhoog trekken en de drempel voor de
            // hele opname bederven.
            const gesorteerd = [...ijkingRef.current].sort((x, y) => x - y);
            const midden = gesorteerd[Math.floor(gesorteerd.length / 2)] ?? 0;
            drempelRef.current = Math.max(DREMPEL_ONDERGRENS, midden * DREMPEL_FACTOR);
            ijkingRef.current = [];
            ijkKlaarRef.current = true;
          }

          // De vloer loopt door, ook nadat de drempel vaststaat. Blijkt de
          // kamer stiller dan bij het ijken, dan zakt de drempel mee.
          vloerRef.current = Math.min(vloerRef.current, sterkte);
          drempelRef.current = Math.min(
            drempelRef.current,
            Math.max(DREMPEL_ONDERGRENS, vloerRef.current * DREMPEL_FACTOR),
          );

          if (sterkte > drempelRef.current) {
            hoordeIetsRef.current = true;
            stilteVanafRef.current = null;
            return;
          }
          // Pas stilte tellen NADAT er iets gezegd is. Anders stopt hij
          // voordat je begonnen bent.
          if (!hoordeIetsRef.current) return;
          if (stilteVanafRef.current === null) stilteVanafRef.current = nu;
          else if (nu - stilteVanafRef.current > (optiesRef.current.pauzeMs ?? PAUZE_MS_STANDAARD)) {
            void afronden();
          }
        }, METING_MS);

        // Harde bovengrens, los van de stiltemeter.
        window.setTimeout(() => {
          if (recorderRef.current === recorder) void afronden();
        }, MAX_OPNAME_MS);
      })
      .catch((error: unknown) => {
        const naam = error instanceof Error ? error.name : '';
        // Elke reden een eigen code, en de tekst erbij komt uit de taal die de
        // gebruiker koos. "Er ging iets mis" laat hem gokken wat hij moet doen.
        const perNaam: Record<string, SpreekProbleem> = {
          NotAllowedError: 'geweigerd',
          NotFoundError: 'geen_microfoon',
          NotReadableError: 'in_gebruik',
          SecurityError: 'geen_https',
        };
        setFout(perNaam[naam] ?? 'onbekend');
        setLuistert(false);
      });
  }, [luistert, afronden, microfoonKlaar]);

  const stop = useCallback(() => {
    void afronden();
  }, [afronden]);

  useEffect(() => {
    return () => {
      recorderRef.current = null;
      microfoonLoslaten();
    };
  }, [microfoonLoslaten]);

  return {
    luistert,
    niveau,
    verstreken,
    maxSeconden: MAX_OPNAME_MS / 1000,
    fout,
    kanOpnemen: opnemenBeschikbaar(),
    start,
    stop,
    laatMicrofoonLos,
  };
}
